import { useTranslation } from 'react-i18next';
import { Tabs } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { MENU_GOVERNANCE_TABS } from '@/Pages/tabRoutes';
import VersionsPanel from './VersionsPanel';
import BranchHistoryPanel from './BranchHistoryPanel';

/**
 * §20 — central menu governance (ADR-0011). Draft → review → publish, with
 * approval separable from authorship, scheduled publication that does not
 * depend on the cron, and rollback as a forward publish rather than a delete.
 *
 * Two tabs, not six screens crammed into one: **Versions** carries the list,
 * the editor, review & approve, the conflict preview and schedule/rollback —
 * all facets of one version, opened together in `VersionDetail`, the same
 * shape `Pages/Billing/Checks.tsx` opens `CheckDetail` in. **History** answers
 * a different question — "what did this branch carry at a moment I name" —
 * off a different endpoint (`GET …/branches/:id/assortment?asOf=`), so it gets
 * its own place in the URL rather than a mode switch buried in the first tab.
 */
export default function MenuGovernance() {
  const { t } = useTranslation();
  const routed = useRoutedTabs('/menu-governance', MENU_GOVERNANCE_TABS);

  if (!routed.open) return routed.redirect;
  const { tabs, current, select } = routed;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">{t('menuGovernance.title')}</h1>
        <p className="text-sm text-ink-muted">{t('menuGovernance.subtitle')}</p>
      </div>

      <Tabs
        tabs={tabs.map((tab) => ({ key: tab.id, label: t(tab.labelKey) }))}
        value={current}
        onChange={select}
        label={t('menuGovernance.title')}
        idPrefix="menu-governance"
      />

      {current === 'versions' && <VersionsPanel />}
      {current === 'history' && <BranchHistoryPanel />}
    </div>
  );
}
